(() => {
  const formatMoney = (cents) => {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') return window.Shopify.formatMoney(cents);
    return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
      style: 'currency',
      currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD'
    }).format(cents / 100);
  };

  class CircPdp {
    constructor(section) {
      this.section = section;
      this.variants = JSON.parse(section.querySelector('[data-product-json]')?.textContent || '[]');
      this.form = section.querySelector('[data-product-form]');
      this.track = section.querySelector('[data-gallery-track]');
      this.mediaItems = [...section.querySelectorAll('[data-media-id]')];
      this.dots = [...section.querySelectorAll('[data-gallery-dot]')];
      this.buyButton = section.querySelector('[data-buy-button]');
      this.buyLabel = section.querySelector('[data-buy-label]');
      this.defaultBuyLabel = this.buyLabel?.textContent.trim() || 'Add to cart';
      this.tierInputs = [...section.querySelectorAll('[data-tier-quantity]')];
      this.modal = section.querySelector('[data-gallery-modal]');
      this.modalImage = section.querySelector('[data-gallery-modal-image]');
      this.modalThumbs = [...section.querySelectorAll('[data-gallery-modal-thumb]')];
      this.pointerStart = null;
      this.lastFocusedElement = null;
      this.initializeTier();
      this.bindEvents();
    }

    initializeTier() {
      if (!this.tierInputs.length) return;
      const selected = this.tierInputs.find((input) => input.checked) || this.tierInputs[0];
      selected.checked = true;
      const quantity = this.section.querySelector('[data-quantity]');
      if (quantity) quantity.value = selected.value;
    }

    selectedTier() {
      return this.tierInputs.find((input) => input.checked);
    }

    selectedDiscountCode() {
      return this.selectedTier()?.dataset.discountCode?.trim() || '';
    }

    bindEvents() {
      this.section.querySelectorAll('[data-options] input').forEach((input) => input.addEventListener('change', () => this.updateVariant()));
      this.tierInputs.forEach((input) => input.addEventListener('change', () => {
        const quantity = this.section.querySelector('[data-quantity]');
        if (quantity) quantity.value = input.value;
      }));
      this.dots.forEach((dot) => dot.addEventListener('click', () => this.showMedia(Number(dot.dataset.galleryDot))));
      this.track?.addEventListener('scroll', () => this.updateDotsFromScroll(), { passive: true });
      this.form?.addEventListener('submit', (event) => this.submit(event));
      this.section.querySelector('[data-bundle-add]')?.addEventListener('click', (event) => this.addBundle(event));

      this.mediaItems.forEach((item, index) => {
        item.addEventListener('pointerdown', (event) => {
          this.pointerStart = { x: event.clientX, y: event.clientY, pointerId: event.pointerId };
        });
        item.addEventListener('pointerup', (event) => {
          if (!this.pointerStart || this.pointerStart.pointerId !== event.pointerId) return;
          const distance = Math.hypot(event.clientX - this.pointerStart.x, event.clientY - this.pointerStart.y);
          this.pointerStart = null;
          if (distance < 8) this.openGallery(index);
        });
        item.addEventListener('pointercancel', () => { this.pointerStart = null; });
        item.addEventListener('click', (event) => {
          event.preventDefault();
          if (event.detail === 0) this.openGallery(index);
        });
      });

      this.modalThumbs.forEach((thumb) => thumb.addEventListener('click', () => this.setModalImage(Number(thumb.dataset.galleryModalThumb))));
      this.section.querySelectorAll('[data-gallery-close]').forEach((button) => button.addEventListener('click', () => this.closeGallery()));
      document.addEventListener('keydown', (event) => {
        if (!this.modal || this.modal.hidden) return;
        if (event.key === 'Escape') this.closeGallery();
        if (event.key === 'ArrowRight') this.stepModal(1);
        if (event.key === 'ArrowLeft') this.stepModal(-1);
      });
    }

    selectedOptions() {
      return [...this.section.querySelectorAll('[data-options] fieldset')].map((fieldset) => fieldset.querySelector('input:checked')?.value);
    }

    updateVariant() {
      const options = this.selectedOptions();
      const variant = this.variants.find((item) => item.options.every((option, index) => option === options[index]));
      if (!variant) return;
      const variantInput = this.section.querySelector('[data-variant-id]');
      if (variantInput) variantInput.value = variant.id;
      const price = this.section.querySelector('[data-price]');
      if (price) price.textContent = formatMoney(variant.price);
      const comparePrice = this.section.querySelector('[data-compare-price]');
      if (comparePrice) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          comparePrice.textContent = formatMoney(variant.compare_at_price);
          comparePrice.classList.remove('is-hidden');
        } else comparePrice.classList.add('is-hidden');
      }
      if (this.buyButton) this.buyButton.disabled = !variant.available;
      if (this.buyLabel) this.buyLabel.textContent = variant.available ? this.defaultBuyLabel : 'Sold out';
      if (variant.featured_media) {
        const index = this.mediaItems.findIndex((item) => Number(item.dataset.mediaId) === Number(variant.featured_media.id));
        if (index >= 0) this.showMedia(index);
      }
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }

    showMedia(index) {
      const media = this.mediaItems[index];
      if (!media) return;
      this.mediaItems.forEach((item) => item.classList.remove('is-active'));
      this.dots.forEach((dot) => dot.classList.remove('is-active'));
      media.classList.add('is-active');
      this.dots.filter((dot) => Number(dot.dataset.galleryDot) === index).forEach((dot) => dot.classList.add('is-active'));
      if (window.matchMedia('(max-width: 749px)').matches) {
        media.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    }

    updateDotsFromScroll() {
      if (!window.matchMedia('(max-width: 749px)').matches || !this.track || !this.mediaItems.length) return;
      const center = this.track.scrollLeft + this.track.clientWidth / 2;
      let closest = 0;
      this.mediaItems.forEach((item, index) => {
        const itemDistance = Math.abs(item.offsetLeft + item.clientWidth / 2 - center);
        const closestDistance = Math.abs(this.mediaItems[closest].offsetLeft + this.mediaItems[closest].clientWidth / 2 - center);
        if (itemDistance < closestDistance) closest = index;
      });
      this.dots.forEach((dot) => dot.classList.toggle('is-active', Number(dot.dataset.galleryDot) === closest));
      this.mediaItems.forEach((item, index) => item.classList.toggle('is-active', index === closest));
    }

    openGallery(index) {
      if (!this.modal || !this.modalImage) return;
      this.lastFocusedElement = document.activeElement;
      this.setModalImage(index);
      this.modal.hidden = false;
      document.documentElement.classList.add('circ-pdp-gallery-open');
      this.modal.querySelector('[data-gallery-close]')?.focus();
    }

    closeGallery() {
      if (!this.modal) return;
      this.modal.hidden = true;
      document.documentElement.classList.remove('circ-pdp-gallery-open');
      this.lastFocusedElement?.focus?.();
    }

    setModalImage(index) {
      const thumb = this.modalThumbs[index];
      const media = this.mediaItems[index];
      if (!this.modalImage || (!thumb && !media)) return;
      this.modalImage.src = thumb?.dataset.galleryModalSrc || media?.dataset.fullImage || '';
      this.modalImage.alt = thumb?.dataset.galleryModalAlt || media?.querySelector('img')?.alt || '';
      this.modalThumbs.forEach((item, itemIndex) => item.classList.toggle('is-active', itemIndex === index));
      this.modalIndex = index;
      thumb?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }

    stepModal(direction) {
      if (!this.modalThumbs.length) return;
      const next = (Number(this.modalIndex || 0) + direction + this.modalThumbs.length) % this.modalThumbs.length;
      this.setModalImage(next);
    }

    redirectWithOptionalDiscount(destination) {
      const root = window.Shopify.routes.root;
      const discountCode = this.selectedDiscountCode();
      if (!discountCode) {
        window.location.href = `${root}${destination}`;
        return;
      }
      window.location.href = `${root}discount/${encodeURIComponent(discountCode)}?redirect=/${destination}`;
    }

    async submit(event) {
      event.preventDefault();
      if (!this.buyButton || !this.form) return;
      this.buyButton.disabled = true;
      try {
        await this.addItems(new FormData(this.form));
        const action = this.section.dataset.successAction;
        if (action === 'checkout') this.redirectWithOptionalDiscount('checkout');
        else if (action === 'cart') this.redirectWithOptionalDiscount('cart');
        else {
          this.buyButton.disabled = false;
          document.dispatchEvent(new CustomEvent('circ-pdp:added', { bubbles: true, detail: { discountCode: this.selectedDiscountCode() } }));
        }
      } catch (error) {
        console.error('Circ PDP add to cart failed:', error);
        this.buyButton.disabled = false;
      }
    }

    async addBundle(event) {
      const button = event.currentTarget;
      button.disabled = true;
      const formData = new FormData();
      formData.append('id', button.dataset.variantId);
      formData.append('quantity', '1');
      try {
        await this.addItems(formData);
        document.dispatchEvent(new CustomEvent('circ-pdp:added', { bubbles: true }));
      } catch (error) {
        console.error('Circ PDP bundle add failed:', error);
      } finally {
        button.disabled = false;
      }
    }

    async addItems(body) {
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body
      });
      if (!response.ok) throw new Error(await response.text());
      return response.json();
    }
  }

  const init = (root = document) => root.querySelectorAll('[data-circ-pdp]').forEach((section) => {
    if (!section.dataset.initialized) {
      section.dataset.initialized = 'true';
      new CircPdp(section);
    }
  });

  document.addEventListener('DOMContentLoaded', () => init());
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();